import { useState } from 'react'

export type SearchTermsState = {
  searchValue: string
  setSearchValue: (value: string) => void
  searchTerms: string[]
  handleSaveSearchTerm: () => void
  handleRemoveSearchTerm: (searchTerm: string) => void
  clearSearchTerms: () => void
}

export type SearchTermsConfig = {
  // For URL state management (like nuqs)
  urlState?: {
    searchTerms: string[]
    setSearchTerms: (terms: string[], resetPage?: boolean) => void
  }
  // For local state management
  localState?: {
    searchTerms: string[]
    setSearchTerms: (terms: string[]) => void
  }
}

/**
 * Shared hook for managing search terms state and handlers.
 * Can work with both URL state (nuqs) and local state management.
 */
export function useSearchTerms(config: SearchTermsConfig): SearchTermsState {
  const [searchValue, setSearchValue] = useState<string>('')

  // Use URL state if provided, otherwise use local state
  const searchTerms = config.urlState?.searchTerms ?? config.localState?.searchTerms ?? []

  const handleSaveSearchTerm = () => {
    const trimmedSearch = searchValue.trim()
    if (trimmedSearch && !searchTerms.includes(trimmedSearch)) {
      const newTerms = [...searchTerms, trimmedSearch]

      if (config.urlState) {
        // For URL state, reset page when adding search terms
        config.urlState.setSearchTerms(newTerms, true)
      } else if (config.localState) {
        config.localState.setSearchTerms(newTerms)
      }
    }
    setSearchValue('')
  }

  const handleRemoveSearchTerm = (searchTerm: string) => {
    const newTerms = searchTerms.filter((term) => term !== searchTerm)

    if (config.urlState) {
      // For URL state, reset page when removing search terms
      config.urlState.setSearchTerms(newTerms, true)
    } else if (config.localState) {
      config.localState.setSearchTerms(newTerms)
    }
  }

  const clearSearchTerms = () => {
    if (config.urlState) {
      config.urlState.setSearchTerms([], true)
    } else if (config.localState) {
      config.localState.setSearchTerms([])
    }
    setSearchValue('')
  }

  return {
    searchValue,
    setSearchValue,
    searchTerms,
    handleSaveSearchTerm,
    handleRemoveSearchTerm,
    clearSearchTerms
  }
}
